import React, { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";

/**
 * AddressAutocomplete — debounced typeahead backed by OpenStreetMap Nominatim.
 *
 * Why Nominatim: free, no API key, AU-biased via `countrycodes=au`. Rate limit
 * is 1 req/sec; 300ms debounce + min-3-char gate keeps us well under it for
 * a single-user form.
 *
 * On select, calls `onSelect({ street, suburb, state, postcode })` so the
 * parent can fan the parsed parts into individual form fields. Falls back
 * gracefully — if the API errors or returns nothing, the input remains a
 * normal manual text field.
 *
 * @param {Object} props
 * @param {string} props.value           - current full street string
 * @param {(v:string)=>void} props.onChange  - typing handler (raw text)
 * @param {(parts:{street:string,suburb:string,state:string,postcode:string})=>void} [props.onSelect]
 * @param {string} [props.placeholder]
 * @param {Object} [props.style]
 * @param {string} [props.id]
 */
export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "Start typing your address…",
  style,
  id,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Close the dropdown when clicking outside.
  useEffect(() => {
    function onDocClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  function handleChange(e) {
    const v = e.target.value;
    onChange(v);

    // Debounce the API call so we don't hammer Nominatim while typing.
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!v || v.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const url =
          `https://nominatim.openstreetmap.org/search?` +
          new URLSearchParams({
            q: v,
            countrycodes: "au",
            format: "json",
            addressdetails: "1",
            limit: "6",
          }).toString();
        const r = await fetch(url, {
          headers: {
            // Nominatim asks for a contact in the UA. Browsers ignore the
            // Referer override but send their own — they include the host
            // domain which Nominatim treats as the application identifier.
            Accept: "application/json",
          },
        });
        if (!r.ok) {
          setSuggestions([]);
          setOpen(false);
          return;
        }
        const data = await r.json();
        setSuggestions(Array.isArray(data) ? data : []);
        setOpen(true);
      } catch {
        setSuggestions([]);
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }, 300);
  }

  function handlePick(s) {
    const a = s.address || {};
    // Compose street: house_number + road. Nominatim splits these.
    const street = [a.house_number, a.road].filter(Boolean).join(" ");
    // Suburb in AU vocabulary maps to several Nominatim keys depending on
    // the locality type. Try in order of preference.
    const suburb =
      a.suburb || a.neighbourhood || a.village || a.town || a.city || "";
    // State abbreviations — Nominatim returns full names ("New South Wales").
    const stateMap = {
      "Australian Capital Territory": "ACT",
      "New South Wales": "NSW",
      "Northern Territory": "NT",
      "Queensland": "QLD",
      "South Australia": "SA",
      "Tasmania": "TAS",
      "Victoria": "VIC",
      "Western Australia": "WA",
    };
    const state = stateMap[a.state] || a.state || "";
    const postcode = a.postcode || "";

    onChange(street);
    if (onSelect) onSelect({ street, suburb, state, postcode });
    setOpen(false);
  }

  const inputStyle = style || {
    width: "100%",
    fontSize: "1rem",
    padding: "16px 18px 16px 44px",
    minHeight: "56px",
    borderRadius: "12px",
    border: "2px solid var(--border, #e5e7eb)",
    boxSizing: "border-box",
    outline: "none",
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <MapPin
        size={16}
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "16px",
          top: "50%",
          transform: "translateY(-50%)",
          color: "var(--muted, #6b7280)",
          pointerEvents: "none",
        }}
      />
      <input
        id={id}
        type="text"
        value={value}
        onChange={handleChange}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        placeholder={placeholder}
        style={inputStyle}
        autoComplete="street-address"
      />
      {loading && (
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            right: "14px",
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: "0.78rem",
            color: "var(--muted, #6b7280)",
          }}
        >
          searching…
        </span>
      )}
      {open && suggestions.length > 0 && (
        <ul
          role="listbox"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            background: "var(--surface, #fff)",
            border: "1px solid var(--border, rgba(140,100,80,0.18))",
            borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
            zIndex: 50,
            maxHeight: "280px",
            overflowY: "auto",
            margin: 0,
            padding: 0,
            listStyle: "none",
          }}
        >
          {suggestions.map((s) => (
            <li
              key={s.place_id}
              role="option"
              aria-selected="false"
              onClick={() => handlePick(s)}
              style={{
                padding: "10px 14px",
                cursor: "pointer",
                fontSize: "0.88rem",
                lineHeight: 1.4,
                color: "var(--text, #1C1C1E)",
                borderBottom: "1px solid var(--border, rgba(140,100,80,0.10))",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255, 138, 0,0.07)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              {s.display_name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
